import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, input, output, signal } from '@angular/core';

export type ComplexCareLogoVariant = 'full' | 'horizontal' | 'icon';
type LogoSize = 'xsmall' | 'small' | 'medium' | 'large' | 'x-large' | '2x-large' | '3x-large' | '4x-large';

interface LogoErrorEvent {
  imagePath: string;
  error: Error;
  timestamp: Date;
}

interface LogoClickEvent {
  variant: ComplexCareLogoVariant;
  timestamp: Date;
  nativeEvent: MouseEvent | KeyboardEvent;
}

const BRANDING_PATH = '/branding/company';
const LOGO_PATHS: Record<ComplexCareLogoVariant, string> = {
  full: `${BRANDING_PATH}/complexcare-full.svg`,
  horizontal: `${BRANDING_PATH}/complexcare-horizontal.svg`,
  icon: `/assets/images/ccs-logo.svg`,
};

/**
 * HxComplexCareLogo - ComplexCare Solutions Company Logo Component
 *
 * Displays ComplexCare Solutions company logo in different variants.
 *
 * @example
 *   ```html
 *   <hx-complexcare-logo variant="full" size="medium" />
 *   <hx-complexcare-logo variant="horizontal" [clickable]="true" (logoClick)="handleClick($event)" />
 *   ```;
 */
@Component({
  selector: 'hx-complexcare-logo',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './complexcare-logo.html',
  styleUrl: './logo.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxComplexCareLogo {
  /** Logo variant to display */
  variant = input<ComplexCareLogoVariant>('full');

  /** Predefined size */
  size = input<LogoSize>('medium');

  /** Custom width override */
  width = input<number | undefined>(undefined);

  /** Custom height override */
  height = input<number | undefined>(undefined);

  /** Custom alt text */
  alt = input<string | undefined>(undefined);

  /** Additional CSS classes */
  cssClass = input<string>('');

  /** Enable clickable mode */
  clickable = input<boolean>(false);

  /** Custom ARIA label */
  ariaLabel = input<string | undefined>(undefined);

  /** Tab index for keyboard navigation */
  tabIndex = input<number>(0);

  /** Enable subtle hover animation (scale effect) */
  hoverEffect = input<boolean>(false);

  /** Emitted when logo is clicked */
  logoClick = output<LogoClickEvent>();

  /** Emitted when image fails to load */
  logoError = output<LogoErrorEvent>();

  /** Internal error state */
  showFallback = signal(false);

  /** Computed logo path */
  logoPath = computed(() => LOGO_PATHS[this.variant()]);

  /** Computed alt text */
  logoAlt = computed(() => {
    if (this.alt()) return this.alt()!;
    const variant = this.variant();
    return `ComplexCare Solutions Logo - ${variant}`;
  });

  /** Computed CSS classes */
  logoClasses = computed(() => {
    const classes = ['hx-logo', `hx-logo--${this.size()}`];
    if (this.clickable()) classes.push('hx-logo--clickable');
    if (this.hoverEffect()) classes.push('hx-logo--hover-effect');
    if (this.cssClass()) classes.push(this.cssClass());
    return classes;
  });

  /** Computed width */
  logoWidth = computed(() => {
    if (this.width()) return `${this.width()}px`;
    const sizeMap = {
      xsmall: '20px',
      small: '24px',
      medium: '48px',
      large: '96px',
      'x-large': '120px',
      '2x-large': '144px',
      '3x-large': '168px',
      '4x-large': '192px',
    };
    return sizeMap[this.size()];
  });

  /** Computed height */
  logoHeight = computed(() => {
    if (this.height()) return `${this.height()}px`;
    const sizeMap = {
      xsmall: '20px',
      small: '24px',
      medium: '48px',
      large: '96px',
      'x-large': '120px',
      '2x-large': '144px',
      '3x-large': '168px',
      '4x-large': '192px',
    };
    return sizeMap[this.size()];
  });

  @HostListener('error', ['$event'])
  onImageError(event: Event) {
    this.showFallback.set(true);
    this.logoError.emit({
      imagePath: this.logoPath(),
      error: new Error('Failed to load logo image'),
      timestamp: new Date(),
    });
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.clickable()) return;
    this.logoClick.emit({
      variant: this.variant(),
      timestamp: new Date(),
      nativeEvent: event,
    });
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.clickable()) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.logoClick.emit({
        variant: this.variant(),
        timestamp: new Date(),
        nativeEvent: event,
      });
    }
  }
}
